import logo from "../../assets/karinfinity-logo.jpg";
import { useState } from "react";
import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { Menu, X, ChevronDown } from "lucide-react";
import { 
  Sheet, 
  SheetContent, 
  SheetTrigger,
  SheetClose
} from "@/components/ui/sheet";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const services = [
  { name: "Analytics", href: "/#analytics" },
  { name: "Data Engineering", href: "/#data-engineering" },
  { name: "CDP", href: "/#cdp" },
  { name: "AI", href: "/#ai" }
];

const Header = () => {
  const [location] = useLocation();
  
  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background">
      <div className="container flex h-16 items-center justify-between py-4">
        <div className="flex items-center">
          <img src={logo} alt="Karinfinity Logo" className="h-10 mr-3" />
          <Link href="/" className="text-2xl font-bold text-primary">
            Karinfinity
          </Link>
        </div>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex space-x-6">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <Link href="/">
                  <NavigationMenuLink className={cn(
                    "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:text-primary",
                    location === "/" && "text-primary"
                  )}>
                    Home
                  </NavigationMenuLink>
                </Link>
              </NavigationMenuItem>
              
              <NavigationMenuItem>
                <NavigationMenuTrigger className="text-sm font-medium">Services</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[200px] gap-3 p-4">
                    {services.map((service) => (
                      <li key={service.name}>
                        <a 
                          href={service.href}
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        >
                          <div className="text-sm font-medium leading-none">{service.name}</div>
                        </a>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
              
              <NavigationMenuItem>
                <a href="/#about">
                  <NavigationMenuLink className="group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:text-primary">
                    About Us
                  </NavigationMenuLink>
                </a>
              </NavigationMenuItem>
              
              <NavigationMenuItem>
                <a href="/#case-studies">
                  <NavigationMenuLink className="group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:text-primary">
                    Case Studies
                  </NavigationMenuLink>
                </a>
              </NavigationMenuItem>
              
              <NavigationMenuItem>
                <Link href="/blog">
                  <NavigationMenuLink className={cn(
                    "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:text-primary",
                    location === "/blog" || location.startsWith("/blog/") ? "text-primary" : ""
                  )}>
                    Blog
                  </NavigationMenuLink>
                </Link>
              </NavigationMenuItem>
              
              <NavigationMenuItem>
                <Link href="/contact">
                  <NavigationMenuLink className={cn(
                    "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:text-primary",
                    location === "/contact" && "text-primary"
                  )}>
                    Contact
                  </NavigationMenuLink>
                </Link>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </nav>
        
        {/* Mobile Navigation */}
        <Sheet>
          <SheetTrigger className="md:hidden" aria-label="Open Menu">
            <Menu className="h-6 w-6" />
          </SheetTrigger>
          <SheetContent side="right">
            <nav className="flex flex-col gap-4 mt-8">
              <SheetClose asChild>
                <Link href="/" className="block py-2 text-lg font-medium hover:text-primary">
                  Home
                </Link>
              </SheetClose>
              
              <div className="relative py-2">
                <MobileSubmenu 
                  title="Services"
                  items={services.map(service => ({
                    label: service.name,
                    href: service.href
                  }))}
                />
              </div>
              
              <SheetClose asChild>
                <a href="/#about" className="block py-2 text-lg font-medium hover:text-primary">
                  About Us
                </a>
              </SheetClose>
              
              <SheetClose asChild>
                <a href="/#case-studies" className="block py-2 text-lg font-medium hover:text-primary">
                  Case Studies
                </a>
              </SheetClose>
              
              <SheetClose asChild>
                <Link href="/blog" className="block py-2 text-lg font-medium hover:text-primary">
                  Blog
                </Link>
              </SheetClose>
              
              <SheetClose asChild>
                <Link href="/contact" className="block py-2 text-lg font-medium hover:text-primary">
                  Contact
                </Link>
              </SheetClose>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

interface MobileSubmenuProps {
  title: string;
  items: { label: string; href: string }[];
}

const MobileSubmenu = ({ title, items }: MobileSubmenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <>
      <button 
        className="flex items-center justify-between w-full text-lg font-medium hover:text-primary"
        onClick={() => setIsOpen(!isOpen)}
      >
        {title}
        <ChevronDown className={cn("h-5 w-5 transition-transform", isOpen && "rotate-180")} />
      </button>
      
      {isOpen && (
        <div className="pl-4 mt-2 space-y-2">
          {items.map((item) => (
            <SheetClose key={item.href} asChild>
              <a 
                href={item.href} 
                className="block py-2 hover:text-primary"
              >
                {item.label}
              </a>
            </SheetClose>
          ))}
        </div>
      )}
    </>
  );
};

export default Header;
